import { c as _c } from "react/compiler-runtime";
import { getKeyFrictionThresholds } from '@/lib/piano/touch-design/data/friction';
import { useTranslation } from '@/hooks/use-translation';
import { DEMO_KEY_COUNT, demoMeasured } from './demo-sample';
import { bandPath, diamondsPath, dotsPath, plotX, plotY, trianglesPath, type PlotBox } from './plot';

/** F5 to E7, the two octaves where the demonstration piano jumps. */
const FROM = 56;
const TO = 79;
/** The key the balance weight steps up on, and by how much. */
const STEP_KEY = 68;
const STEP_GRAMS = 12;
const WEIGHTS: PlotBox = {
  from: FROM,
  height: 88,
  max: 75,
  min: 30,
  to: TO,
  width: 252,
  x: 12,
  y: 8
};
const FRICTION: PlotBox = {
  ...WEIGHTS,
  height: 38,
  max: 20,
  min: 4,
  y: 108
};

/** The grid the weights are read against, in grams. */
const GRID = [40, 50, 60, 70];

/**
 * The reference zones, as the friction chart computes them: the middle of the
 * band drifts down from the bass to the treble, so they are sloping ribbons
 * rather than stripes.
 */
const thresholds = Array.from({
  length: DEMO_KEY_COUNT
}, (_, index) => getKeyFrictionThresholds(index + 1, DEMO_KEY_COUNT));
const ZONES = [{
  bounds: thresholds.map(zones => zones.highZone),
  className: 'fill-ptl-zone-high'
}, {
  bounds: thresholds.map(zones => zones.mediumZone),
  className: 'fill-ptl-zone-medium'
}, {
  bounds: thresholds.map(zones => zones.lowZone),
  className: 'fill-ptl-zone-low'
}];

/** Down weight and up weight tied together, key by key. */
const ranges = () => {
  let path = '';
  for (let index = FROM; index <= TO; index += 1) {
    const down = demoMeasured.downWeight[index];
    const up = demoMeasured.upWeight[index];
    if (down == null || up == null) {
      continue;
    }
    path += `M${plotX(WEIGHTS, index)} ${plotY(WEIGHTS, down)}V${plotY(WEIGHTS, up)}`;
  }
  return path;
};

/**
 * A slice of the static balance figure: the irregularity you are looking for.
 *
 * The demonstration piano jumps twelve grams between E6 and F6 without the
 * friction moving, which is exactly the kind of thing the page is promising
 * to show — so the illustration rings it.
 */
export const StaticBalancePreview = () => {
  const $ = _c(47);
  const {
    t
  } = useTranslation();
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = plotX(WEIGHTS, STEP_KEY - 0.5);
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const ringCenter = t0;
  let t1;
  if ($[1] !== t) {
    t1 = t("An extract of the static balance figure, keys F5 to E7: down, balance and up weight, and the friction with its reference zones; a 12 g step is ringed between E6 and F6, with no change in friction.");
    $[1] = t;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t10;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <path d={GRID.map(_temp).join("")} strokeWidth="1" strokeDasharray="2 3" fill="none" className="stroke-ptl-chart-grid" />;
    t3 = <path d="M0 101.5H276" strokeWidth="1" fill="none" className="stroke-input" />;
    t4 = ZONES.map(_temp2);
    t5 = <path d={ranges()} strokeWidth="1.2" fill="none" className="stroke-ptl-chart-range" />;
    t6 = <path d={trianglesPath(demoMeasured.downWeight, WEIGHTS, {
      halfWidth: 3.4,
      height: 5.6
    }, "down")} className="fill-ptl-series-down" />;
    t7 = <path d={trianglesPath(demoMeasured.upWeight, WEIGHTS, {
      halfWidth: 3.4,
      height: 5.6
    }, "up")} className="fill-ptl-series-up" />;
    t8 = <path d={diamondsPath(demoMeasured.balanceWeight, WEIGHTS, 3.6)} className="fill-ptl-series-balance" />;
    t9 = <path d={dotsPath(demoMeasured.friction, FRICTION, 2.2)} className="fill-ptl-series-friction" />;
    t10 = <ellipse cx={ringCenter} cy="52" rx="15" ry="43" strokeWidth="1.3" strokeDasharray="3 3" fill="none" className="stroke-ptl-current" />;
    $[3] = t10;
    $[4] = t2;
    $[5] = t3;
    $[6] = t4;
    $[7] = t5;
    $[8] = t6;
    $[9] = t7;
    $[10] = t8;
    $[11] = t9;
  } else {
    t10 = $[3];
    t2 = $[4];
    t3 = $[5];
    t4 = $[6];
    t5 = $[7];
    t6 = $[8];
    t7 = $[9];
    t8 = $[10];
    t9 = $[11];
  }
  let t11;
  if ($[12] !== t) {
    t11 = t("+{{weight}} g", {
      weight: STEP_GRAMS
    });
    $[12] = t;
    $[13] = t11;
  } else {
    t11 = $[13];
  }
  let t12;
  if ($[14] !== t11) {
    t12 = <text x={plotX(WEIGHTS, STEP_KEY - 1) - 16} y="13" textAnchor="end" className="ptl-halo fill-ptl-current font-mono text-[10px]">{t11}</text>;
    $[14] = t11;
    $[15] = t12;
  } else {
    t12 = $[15];
  }
  let t13;
  if ($[16] !== t) {
    t13 = t("weight");
    $[16] = t;
    $[17] = t13;
  } else {
    t13 = $[17];
  }
  let t14;
  if ($[18] !== t13) {
    t14 = <text x="318" y="20" textAnchor="end" className="fill-ptl-ink-3 font-mono text-[7.5px]">{t13}</text>;
    $[18] = t13;
    $[19] = t14;
  } else {
    t14 = $[19];
  }
  let t15;
  if ($[20] !== t) {
    t15 = t("friction");
    $[20] = t;
    $[21] = t15;
  } else {
    t15 = $[21];
  }
  let t16;
  if ($[22] !== t15) {
    t16 = <text x="318" y="98" textAnchor="end" className="fill-ptl-ink-3 font-mono text-[7.5px]">{t15}</text>;
    $[22] = t15;
    $[23] = t16;
  } else {
    t16 = $[23];
  }
  let t17;
  if ($[24] !== t) {
    t17 = t("High");
    $[24] = t;
    $[25] = t17;
  } else {
    t17 = $[25];
  }
  let t18;
  if ($[26] !== t17) {
    t18 = {
      label: t17,
      y: 115
    };
    $[26] = t17;
    $[27] = t18;
  } else {
    t18 = $[27];
  }
  let t19;
  if ($[28] !== t) {
    t19 = t("Medium");
    $[28] = t;
    $[29] = t19;
  } else {
    t19 = $[29];
  }
  let t20;
  if ($[30] !== t19) {
    t20 = {
      label: t19,
      y: 127
    };
    $[30] = t19;
    $[31] = t20;
  } else {
    t20 = $[31];
  }
  let t21;
  if ($[32] !== t) {
    t21 = t("Low");
    $[32] = t;
    $[33] = t21;
  } else {
    t21 = $[33];
  }
  let t22;
  if ($[34] !== t21) {
    t22 = {
      label: t21,
      y: 139
    };
    $[34] = t21;
    $[35] = t22;
  } else {
    t22 = $[35];
  }
  let t23;
  if ($[36] !== t18 || $[37] !== t20 || $[38] !== t22) {
    t23 = [t18, t20, t22].map(_temp3);
    $[36] = t18;
    $[37] = t20;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] === Symbol.for("react.memo_cache_sentinel")) {
    t24 = [{
      label: "C6",
      number: 64
    }, {
      label: "C7",
      number: 76
    }].map(_temp4);
    $[40] = t24;
  } else {
    t24 = $[40];
  }
  let t25;
  if ($[41] !== t1 || $[42] !== t12 || $[43] !== t14 || $[44] !== t16 || $[45] !== t23) {
    t25 = <svg viewBox="0 0 320 156" className="block min-h-0 w-full flex-1" role="img" aria-label={t1}>{t2}{t3}{t4}{t5}{t6}{t7}{t8}{t9}{t10}{t12}{t14}{t16}{t23}{t24}</svg>;
    $[41] = t1;
    $[42] = t12;
    $[43] = t14;
    $[44] = t16;
    $[45] = t23;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  return t25;
};
function _temp(weight) {
  return `M0 ${plotY(WEIGHTS, weight)}H276`;
}
function _temp2(zone) {
  return <path key={zone.className} d={bandPath(zone.bounds, FRICTION, 10)} className={zone.className} />;
}
function _temp3(zone_0) {
  return <text key={zone_0.y} x="318" y={zone_0.y} textAnchor="end" className="fill-ptl-ink-3 font-mono text-[7.5px]">{zone_0.label}</text>;
}
function _temp4(octave) {
  return <text key={octave.label} x={plotX(WEIGHTS, octave.number - 1)} y="155" textAnchor="middle" className="fill-ptl-ink-3 font-mono text-[8px]">{octave.label}</text>;
}